"use client";

import Link from "next/link";
import Image from "next/image";
import { ShoppingBag, Plus, Check } from "lucide-react";
import { useCart } from "@/lib/context/cart-context";
import { useState } from "react";
import { createProductSlug } from "@/lib/slug-utils";
// Display tax-inclusive prices to match product detail page + cart subtotal.
// Dutchie API returns priceRec/specialPriceRec with ~13% NY tax already
// baked in; we display them raw. (Previously these were piped through
// getPricePreTax which made the specials page show pre-tax prices while
// every other surface — product detail, cart, checkout — showed with-tax.)

interface ProductVariant {
  id: string;
  option: string;
  priceRec: number;
  specialPriceRec?: number;
}

interface Product {
  id: string;
  name: string;
  brand?: { name: string };
  category: string;
  strainType?: string;
  image?: string;
  variants: ProductVariant[];
}

interface SpecialProductCardProps {
  product: Product;
  darkMode?: boolean;
}

// Build the canonical product URL slug the same way the product page does
// (brand + name + strain + category), so deal links never 404. brand comes
// in as { name } here, which createProductSlug handles.
function productHref(product: Product): string {
  return `/shop/product/${createProductSlug(product as any)}`;
}

export default function SpecialProductCard({ product, darkMode = false }: SpecialProductCardProps) {
  const { addToCart } = useCart();
  const [isAdding, setIsAdding] = useState(false);
  const [justAdded, setJustAdded] = useState(false);

  const variant = product.variants?.[0];
  const hasSpecialPrice = variant?.specialPriceRec && variant.specialPriceRec < variant.priceRec;
  const displayPrice = hasSpecialPrice ? variant.specialPriceRec! : variant?.priceRec;
  const savings = hasSpecialPrice
    ? Math.round(((variant.priceRec - variant.specialPriceRec!) / variant.priceRec) * 100)
    : 0;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!variant || isAdding) return;
    
    setIsAdding(true);
    try {
      // Build ProductDisplay format that cart expects
      // Critical: variantId is needed for Dutchie checkout API
      const brandName = product.brand?.name || "Unknown";
      const optionText = variant.option || "";
      const fullName = optionText ? `${product.name} - ${optionText}` : product.name;
      
      const productDisplay = {
        id: product.id,
        variantId: variant.id, // Critical for checkout!
        name: product.name,
        option: optionText,
        fullName: fullName,
        category: product.category,
        brand: brandName,
        image: product.image || "",
        price: variant.priceRec,
        specialPrice: hasSpecialPrice ? variant.specialPriceRec : undefined,
        inStock: true,
        staffPick: false,
        collections: [],
      };
      addToCart(productDisplay as any, 1);
      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 2000);
    } catch (error) {
      console.error("Failed to add to cart:", error);
    } finally {
      setIsAdding(false);
    }
  };

  if (darkMode) {
    return (
      <div className="group relative bg-white/10 backdrop-blur-sm rounded-lg overflow-hidden hover:bg-white/20 transition-all">
        <Link href={productHref(product)}>
          <div className="relative aspect-square bg-white/5">
            {product.image ? (
              <Image
                src={product.image}
                alt={product.name}
                fill
                className="object-contain p-2 group-hover:scale-105 transition-transform"
                sizes="(max-width: 768px) 50vw, 16vw"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/30">
                <ShoppingBag className="h-8 w-8" />
              </div>
            )}
            {savings > 0 && (
              <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                {savings}% OFF
              </span>
            )}
          </div>
          <div className="p-3">
            <p className="text-xs text-white/60 mb-1">{product.brand?.name || product.category}</p>
            <h4 className="text-sm font-medium text-white line-clamp-2 mb-2">{product.name}</h4>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {hasSpecialPrice ? (
                  <>
                    <span className="text-sm font-bold text-gold">${variant.specialPriceRec!.toFixed(2)}</span>
                    <span className="text-xs text-white/40 line-through">${variant.priceRec.toFixed(2)}</span>
                  </>
                ) : variant?.priceRec ? (
                  <span className="text-sm font-bold text-gold">${variant.priceRec.toFixed(2)}</span>
                ) : null}
              </div>
            </div>
          </div>
        </Link>
        {/* Add to Cart Button */}
        <button
          onClick={handleAddToCart}
          disabled={isAdding || !variant}
          className={`absolute bottom-3 right-3 p-2 rounded-full transition-all ${
            justAdded 
              ? 'bg-green-500 text-white' 
              : 'bg-white/20 hover:bg-white/30 text-white'
          }`}
        >
          {justAdded ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </button>
      </div>
    );
  }

  return (
    <div className="group relative bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden border border-gray-100 hover:border-gray-200">
      <Link href={productHref(product)}>
        {/* Image container - square on mobile, slightly taller on desktop */}
        <div className="relative aspect-square sm:aspect-[5/6] bg-gradient-to-b from-gray-50 to-white overflow-hidden">
          {product.image ? (
            <Image
              src={product.image}
              alt={product.name}
              fill
              className="object-contain p-1 group-hover:scale-105 transition-transform duration-300"
              sizes="(max-width: 640px) 45vw, (max-width: 768px) 30vw, 16vw"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-200">
              <ShoppingBag className="h-6 w-6" />
            </div>
          )}
          {/* Sale badge */}
          {savings > 0 && (
            <span className="absolute top-1.5 left-1.5 sm:top-2 sm:left-2 bg-gradient-to-r from-red-500 to-red-600 text-white text-[10px] sm:text-xs font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full shadow-sm">
              {savings}% OFF
            </span>
          )}
        </div>
        
        {/* Content - compact */}
        <div className="p-1.5 sm:p-2.5 border-t border-gray-50">
          <p className="text-[8px] sm:text-[10px] font-semibold uppercase tracking-wide text-primary_green">
            {product.brand?.name || 'Brand'}
          </p>
          <h4 className="text-[11px] sm:text-xs font-medium text-gray-800 line-clamp-2 leading-tight group-hover:text-primary_green transition-colors mt-0.5">
            {product.name}
          </h4>
          
          {/* Price row */}
          <div className="mt-1 flex items-center justify-between">
            <div className="flex items-center gap-1">
              {hasSpecialPrice ? (
                <>
                  <span className="text-xs sm:text-sm font-bold text-red-600">${variant.specialPriceRec!.toFixed(2)}</span>
                  <span className="text-[9px] sm:text-[10px] text-gray-400 line-through">${variant.priceRec.toFixed(2)}</span>
                </>
              ) : variant?.priceRec ? (
                <span className="text-xs sm:text-sm font-bold text-gray-900">${variant.priceRec.toFixed(2)}</span>
              ) : null}
            </div>
          </div>
        </div>
      </Link>
      
      {/* Add to Cart Button */}
      <button
        onClick={handleAddToCart}
        disabled={isAdding || !variant}
        className={`absolute bottom-1.5 right-1.5 sm:bottom-2.5 sm:right-2.5 w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-full transition-all duration-200 shadow-md ${
          justAdded 
            ? 'bg-green-500 text-white scale-110' 
            : 'bg-primary_green hover:bg-green-700 hover:scale-110 text-white'
        }`}
      >
        {justAdded ? <Check className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> : <Plus className="h-3 w-3 sm:h-3.5 sm:w-3.5" />}
      </button>
    </div>
  );
}
